import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
// Using standard HTML elements instead of UI components
import { CheckCircle, XCircle, Clock, Users, Mail } from 'lucide-react';
import { acceptFamilyInvitationWeb } from '../services/WebCloudFunctionsService';
import { getAuth } from 'firebase/auth';
import { initializeApp, getApps } from 'firebase/app';
import { firebaseConfig } from '../config/firebase';

export const AcceptFamilyInvitation: React.FC = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);
    const [status, setStatus] = useState<'loading' | 'success' | 'error' | 'expired'>('loading');
    const [message, setMessage] = useState('');
    const [invitationData, setInvitationData] = useState<any>(null);

    const token = searchParams.get('token');
    // Support both the new `invitationId` query param and legacy `id`
    const invitationId = searchParams.get('invitationId') || searchParams.get('id');

    useEffect(() => {
        if (!token || !invitationId) {
            setStatus('error');
            setMessage('Invalid invitation link');
            return;
        }

        // Check if user is authenticated
        const app = getApps().length === 0 ? initializeApp(firebaseConfig) : getApps()[0];
        const auth = getAuth(app);
        const user = auth.currentUser;

        if (!user) {
            // User is not authenticated, redirect to login with return URL
            const currentUrl = window.location.href;
            navigate(`/login?returnUrl=${encodeURIComponent(currentUrl)}`);
            return;
        }

        // Auto-accept the invitation
        handleAcceptInvitation();
    }, [token, invitationId, navigate]);

    const handleAcceptInvitation = async () => {
        if (!token || !invitationId) return;

        setIsLoading(true);
        setStatus('loading');

        try {
            const result = await acceptFamilyInvitationWeb(invitationId, token) as any;

            if (result.success) {
                setStatus('success');
                setMessage('Welcome to the family! You can now participate in organization challenges.');

                // Redirect to home page after 3 seconds
                setTimeout(() => {
                    navigate('/');
                }, 3000);
            } else {
                setStatus('error');
                setMessage(result.error || 'Failed to accept invitation');
            }
        } catch (error) {
            setStatus('error');
            setMessage('An unexpected error occurred');
        } finally {
            setIsLoading(false);
        }
    };

    const getStatusIcon = () => {
        switch (status) {
            case 'success':
                return <CheckCircle className="h-16 w-16 text-green-500" />;
            case 'error':
            case 'expired':
                return <XCircle className="h-16 w-16 text-red-500" />;
            case 'loading':
                return <Clock className="h-16 w-16 text-blue-500" />;
            default:
                return <Clock className="h-16 w-16 text-gray-500" />;
        }
    };

    const getStatusColor = () => {
        switch (status) {
            case 'success':
                return 'text-green-600';
            case 'error':
            case 'expired':
                return 'text-red-600';
            case 'loading':
                return 'text-blue-600';
            default:
                return 'text-gray-600';
        }
    };

    const getStatusTitle = () => {
        switch (status) {
            case 'success':
                return 'Invitation Accepted!';
            case 'error':
                return 'Error';
            case 'expired':
                return 'Invitation Expired';
            case 'loading':
                return 'Processing...';
            default:
                return 'Processing...';
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 flex items-center justify-center p-4">
            <div className="w-full max-w-md shadow-2xl border-0 bg-white rounded-lg">
                <div className="text-center pb-4 p-6">
                    <div className="flex justify-center mb-4">
                        {getStatusIcon()}
                    </div>
                    <h1 className={`text-2xl font-bold ${getStatusColor()}`}>
                        {getStatusTitle()}
                    </h1>
                </div>

                <div className="text-center space-y-4 p-6">
                    {status === 'loading' && (
                        <div className="space-y-4">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                            <p className="text-gray-600">Accepting your family invitation...</p>
                        </div>
                    )}

                    {status === 'success' && (
                        <div className="space-y-4">
                            <div className="bg-green-50 rounded-lg p-4">
                                <div className="flex items-center justify-center mb-2">
                                    <Users className="h-8 w-8 text-green-600" />
                                </div>
                                <p className="text-green-800 font-medium">
                                    {message}
                                </p>
                            </div>

                            <div className="bg-blue-50 rounded-lg p-4">
                                <div className="flex items-center justify-center mb-2">
                                    <Mail className="h-6 w-6 text-blue-600" />
                                </div>
                                <p className="text-blue-800 text-sm">
                                    You'll be redirected to the home page in a few seconds...
                                </p>
                            </div>

                            <button
                                onClick={() => navigate('/')}
                                className="w-full bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg"
                            >
                                Go to Home Page
                            </button>
                        </div>
                    )}

                    {status === 'error' && (
                        <div className="space-y-4">
                            <div className="bg-red-50 rounded-lg p-4">
                                <p className="text-red-800">
                                    {message}
                                </p>
                            </div>

                            <button
                                onClick={() => navigate('/')}
                                className="w-full bg-gray-600 hover:bg-gray-700 text-white px-6 py-2 rounded-lg"
                            >
                                Go to Home Page
                            </button>
                        </div>
                    )}

                    {status === 'expired' && (
                        <div className="space-y-4">
                            <div className="bg-yellow-50 rounded-lg p-4">
                                <p className="text-yellow-800">
                                    This invitation has expired. Please contact the person who invited you for a new invitation.
                                </p>
                            </div>

                            <button
                                onClick={() => navigate('/')}
                                className="w-full bg-gray-600 hover:bg-gray-700 text-white px-6 py-2 rounded-lg"
                            >
                                Go to Home Page
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
